import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';

// آدمک‌های واقعی (Mixamo → GLB با blender/build_avatars.py روی سرور، در /avatars/). هر آدم: مدل + اسکلت + سه
// انیمیشن: idle (ایستادن)، walk (راه رفتن)، talk (حرف زدن). این‌جا: بارگذاری (یک بار برای هر آدم، بعد کپیِ سبک برای
// هر نفر)، و پخشِ نرمِ انیمیشن‌ها بر اساسِ سرعت و «در حالِ صحبت».
//
// قرارداد: واحدِ صحنه‌ی ساختمان سانتی‌متر است و رو به −Z محلی (مثلِ دوربینِ اول‌شخص)؛ مدل‌های glTF رو به +Z‌اند.

const BASE = '/avatars/';
const WALK_CM_PER_S = 160; // سرعتِ راه رفتنِ خودِ انیمیشن (یک چرخه ≈ ۰٫۹۷ ثانیه، ≈ ۱٫۵۵ متر)

let manifestPromise = null;
export function loadAvatarManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${BASE}avatars.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : { avatars: [] }))
      .then((j) => (Array.isArray(j.avatars) ? j.avatars.map((a) => ({ ...a, url: BASE + a.file, thumbUrl: BASE + a.thumb })) : []))
      .catch(() => {
        manifestPromise = null;
        return [];
      });
  }
  return manifestPromise;
}

export function useAvatarManifest() {
  const [list, setList] = useState(null);
  useEffect(() => {
    let alive = true;
    loadAvatarManifest().then((l) => alive && setList(l));
    return () => {
      alive = false;
    };
  }, []);
  return list;
}

const assets = new Map(); // id → Promise<{ scene, clips, heightCm }>

// «درجا» کردنِ راه رفتن: جابه‌جاییِ افقیِ لگن حذف (حرکت را خودِ برنامه می‌دهد)
function inPlace(clip) {
  clip.tracks.forEach((t) => {
    if (!/Hips\.position$/.test(t.name)) return;
    const v = t.values;
    const x0 = v[0];
    const z0 = v[2];
    for (let i = 0; i < v.length; i += 3) {
      v[i] = x0;
      v[i + 2] = z0;
    }
  });
  return clip;
}

function loadAsset(id) {
  if (assets.has(id)) return assets.get(id);
  const p = loadAvatarManifest().then((list) => {
    const meta = list.find((a) => a.id === id);
    if (!meta) throw new Error('unknown-avatar');
    return new Promise((resolve, reject) => {
      new GLTFLoader().load(
        meta.url,
        (gltf) => {
          const scene = gltf.scene;
          scene.traverse((o) => {
            if (o.isMesh || o.isSkinnedMesh) {
              o.frustumCulled = false; // اسکلت جابه‌جا می‌شود؛ جعبه‌ی ثابت درست نیست
              o.castShadow = false;
              o.raycast = () => null;
              const mats = Array.isArray(o.material) ? o.material : [o.material];
              mats.forEach((m) => {
                if (!m) return;
                m.envMapIntensity = 0.6;
                if (m.map) m.map.anisotropy = 4;
              });
            }
          });
          const clips = {};
          gltf.animations.forEach((c) => {
            const name = /walk/i.test(c.name) ? 'walk' : /talk|argu/i.test(c.name) ? 'talk' : /idle/i.test(c.name) ? 'idle' : null;
            if (name && !clips[name]) clips[name] = name === 'walk' ? inPlace(c) : c;
          });
          const box = new THREE.Box3().setFromObject(scene);
          const h = box.max.y - box.min.y;
          // معمولاً متر است (۱٫۵ تا ۲ متر)؛ اگر نبود، به قدِ ۱۷۲ سانت می‌رسانیم
          const heightCm = h > 1.2 && h < 2.3 ? h * 100 : 172;
          const scale = h > 1.2 && h < 2.3 ? 100 : 172 / Math.max(h, 1e-3);
          resolve({ scene, clips, heightCm, scale, minY: box.min.y });
        },
        undefined,
        reject
      );
    });
  });
  p.catch(() => assets.delete(id));
  assets.set(id, p);
  return p;
}

export function useAvatarAsset(id) {
  const [asset, setAsset] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!id || id === 'm' || id === 'f') {
      setFailed(true);
      return undefined;
    }
    let alive = true;
    setFailed(false);
    loadAsset(id)
      .then((a) => alive && setAsset(a))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [id]);
  return { asset, failed };
}

// یک نفر — motionRef.current = { speed (سانتی‌متر بر ثانیه)، speaking } هر فریم از بیرون پر می‌شود
export function RealFigure({ asset, motionRef }) {
  const model = useMemo(() => cloneSkinned(asset.scene), [asset]);
  const mixer = useMemo(() => new THREE.AnimationMixer(model), [model]);
  const actions = useMemo(() => {
    const out = {};
    Object.entries(asset.clips).forEach(([k, clip]) => {
      const a = mixer.clipAction(clip);
      a.enabled = true;
      a.setEffectiveWeight(k === 'idle' ? 1 : 0);
      a.play();
      // هر نفر از جای متفاوتی از انیمیشن شروع کند (همه هم‌زمان تکان نخورند)
      a.time = Math.random() * clip.duration;
      out[k] = a;
    });
    return out;
  }, [asset, mixer]);
  const weights = useRef({ idle: 1, walk: 0, talk: 0 });

  useEffect(
    () => () => {
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
    },
    [mixer, model]
  );

  useFrame((_, delta) => {
    const dt = Math.min(0.1, delta);
    const m = (motionRef && motionRef.current) || {};
    const walking = (m.speed || 0) > 12;
    const target = {
      walk: walking && actions.walk ? 1 : 0,
      talk: !walking && m.speaking && actions.talk ? 1 : 0,
    };
    target.idle = 1 - target.walk - target.talk;
    const k = 1 - Math.exp(-dt * 6);
    Object.keys(weights.current).forEach((name) => {
      weights.current[name] += ((target[name] || 0) - weights.current[name]) * k;
      if (actions[name]) actions[name].setEffectiveWeight(weights.current[name]);
    });
    if (actions.walk) actions.walk.setEffectiveTimeScale(walking ? Math.min(1.6, Math.max(0.6, (m.speed || 0) / WALK_CM_PER_S)) : 1);
    mixer.update(dt);
  });

  return (
    <group rotation={[0, Math.PI, 0]} scale={asset.scale} position={[0, -asset.minY * asset.scale, 0]}>
      <primitive object={model} />
    </group>
  );
}
